import {
  IconActivity, IconFileDollar, IconFiles, IconLayoutKanban, IconListCheck, IconLock, IconNotebook, IconPlus, IconRocket, IconSettings, IconUsers,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { fileApi, noteApi, projectApi, stageApi } from '@/api/endpoints'
import type { Project } from '@/api/types'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { RoleBadge, VisibilityBadge } from '@/components/common/RoleBadge'
import { StagePicker } from '@/components/tasks/StageControls'
import { projectCan, type Can } from '@/hooks/usePermissions'
import { tr } from '@/i18n/tr'
import { AvatarStack, Breadcrumbs, Button, ButtonLink, CrumbSep, Monogram, Tabs, tabPanelProps, type TabDef } from '@/ui'
import { projectColor } from '../../../packages/shared/src/tokens'
import { PROJECT_TABS, type ProjectTabKey } from '../../../packages/shared/src/permissions'
import './project.css'

const TAB_ID = 'proje'

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const query = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.get(projectId!),
    enabled: !!projectId,
  })

  /* Sekme sayaçları. Sayfa başına iki hafif istek (size=1, yalnızca totalElements okunur).
     Ayrı anahtar: sekmelerin kendi listeleri ezilmesin. İzin yoksa sunucu 403 verir;
     izinler öğrenilmeden istenmez. */
  const can = projectCan(query.data)
  const noteCount = useQuery({
    queryKey: ['note-count', projectId],
    queryFn: () => noteApi.list(projectId!, { size: 1 }).then((p) => p.totalElements),
    enabled: !!projectId && can('notes.view'),
  })
  const fileCount = useQuery({
    queryKey: ['file-count', projectId],
    queryFn: () => fileApi.list(projectId!, { size: 1 }).then((p) => p.totalElements),
    enabled: !!projectId && can('files.view'),
  })
  // Başlıktaki üye avatarları; Üyeler sekmesiyle aynı önbellek.
  const members = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => projectApi.members(projectId!),
    enabled: !!projectId && can('members.view') && query.data?.visibility === 'SHARED',
  })

  /* Proje durumu (Analiz, Onay bekliyor…). Durum listesi tüm projelerde ortak. */
  const progress = useQuery({
    queryKey: ['progress', projectId],
    queryFn: () => stageApi.progress(projectId!),
    enabled: !!projectId,
  })
  const stages = useQuery({ queryKey: ['project-stages'], queryFn: stageApi.list, staleTime: 5 * 60_000 })

  if (query.isLoading) return <div className="nx-page"><LoadingState /></div>
  if (query.isError) {
    // Silinmiş ya da erişim yetkisi olmayan bir projede tekrar denemek sonuç vermez;
    // kullanıcıya listeye dönecek bir kapı bırakılmalı.
    const durum = (query.error as { response?: { status?: number } })?.response?.status
    const tekrarDenemeyeDeger = !durum || ![403, 404].includes(durum)
    return (
      <div className="nx-page">
        <ErrorState error={query.error} onRetry={tekrarDenemeyeDeger ? () => query.refetch() : undefined}
          action={<ButtonLink to="/projeler" size="sm" variant="secondary">{tr.project.title}</ButtonLink>} />
      </div>
    )
  }
  if (!query.data) return null

  const project = query.data
  const allowed = allowedTabs(can)
  const base = `/projeler/${project.id}`
  const segment = location.pathname.split('/')[3] ?? allowed[0]

  // Sekme izinlerden gelir. İzni olmayan sekmenin adresi (elle yazılan /kasa gibi) ilk
  // izinli sekmeye döner; sunucu zaten 403 verir, boş hata ekranı yerine.
  if (segment && !allowed.includes(segment as ProjectTabKey) && allowed.length > 0) {
    return <Navigate to={`${base}/${allowed[0]}${location.search}`} replace />
  }

  const icon = { size: 17, stroke: 1.8 }
  // İzin-güdümlü sekmeler (dinamik roller): sıra ve görünürlük PROJECT_TABS + allowed'dan gelir.
  // Deploy sekmesi prod'dan geldi; TAB_UI'ya eklendi, görünürlüğü PROJECT_TABS'taki izne bağlı.
  const TAB_UI: Record<ProjectTabKey, Omit<TabDef<string>, 'value'>> = {
    notlar: { label: tr.project.notes, icon: <IconNotebook {...icon} />, count: noteCount.data },
    dosyalar: { label: tr.project.files, icon: <IconFiles {...icon} />, count: fileCount.data },
    gorevler: { label: tr.project.tasks, icon: <IconLayoutKanban {...icon} /> },
    kasa: { label: tr.project.vault, icon: <IconLock {...icon} /> },
    deploy: { label: tr.project.deploy, icon: <IconRocket {...icon} /> },
    teklifler: { label: tr.project.offers, icon: <IconFileDollar {...icon} /> },
    uyeler: { label: tr.project.members, icon: <IconUsers {...icon} />, count: project.visibility === 'SHARED' ? project.memberCount : undefined },
    hareketler: { label: tr.project.activity, icon: <IconActivity {...icon} /> },
    testler: { label: tr.project.tests, icon: <IconListCheck {...icon} /> },
    ayarlar: { label: tr.project.settings, icon: <IconSettings {...icon} /> },
  }
  const tabs: TabDef<string>[] = allowed.map((key) => ({ value: key, ...TAB_UI[key] }))

  const people = (members.data ?? []).map((m) => ({ id: m.userId, name: m.displayName }))
  const context = { project, can }

  // Not editörü kendi çubuğunu çizer (gezinme yolu, kayıt durumu, eylemler); proje
  // başlığı ve sekmeler belgenin önüne geçmesin.
  if (/^\/projeler\/[^/]+\/notlar\/[^/]+/.test(location.pathname)) {
    return <Outlet context={context} />
  }

  return (
    <>
      <header className="nx-project-head">
        <div className="nx-project-head__inner">
          <Breadcrumbs>
            <Link to="/projeler">{tr.project.title}</Link>
            <CrumbSep />
            <span aria-current="page">{project.name}</span>
          </Breadcrumbs>

          <div className="nx-project-head__row">
            <Monogram name={project.name} color={projectColor(project.id)} size={54} radius={15} />
            <div className="nx-project-head__title">
              <h1>{project.name}</h1>
              <div className="nx-project-head__meta">
                <VisibilityBadge visibility={project.visibility} />
                {project.myRole && <RoleBadge role={project.myRole} name={project.roleNames?.[0]} />}
                {progress.data && stages.data && (
                  <StagePicker projectId={project.id} stages={stages.data} stageId={progress.data.stageId}
                    note={progress.data.note} canEdit={can('tasks.edit')} />
                )}
                <span>{project.ownerDisplayName}</span>
                {project.description && <><span aria-hidden="true">·</span><span className="nx-project-head__desc">{project.description}</span></>}
              </div>
            </div>
            <div className="nx-project-head__actions">
              {people.length > 1 && (
                <Link to={`${base}/uyeler`} aria-label={`${people.length} üye`} className="nx-project-head__people">
                  <AvatarStack people={people} max={4} size={32} />
                </Link>
              )}
              {/* Eylem olduğu için düğme: not, Notlar sekmesinde seçili klasörde oluşturulur. */}
              {can('notes.edit') && (
                <Button leftIcon={<IconPlus size={16} stroke={2.2} aria-hidden="true" />}
                  onClick={() => navigate(`${base}/notlar?yeni=1`)}>Yeni not</Button>
              )}
            </div>
          </div>

          <div className="nx-project-head__tabs">
            <Tabs aria-label="Proje sekmeleri" idBase={TAB_ID} value={segment} tabs={tabs}
              onChange={(v) => navigate(`${base}/${v}`)} />
          </div>
          {/* Sekmeler gezinme içindir; içerik <Outlet>'te çizilir. Yine de her sekmenin
              aria-controls'ü DOM'da gerçek bir panele işaret etmeli (axe, critical). */}
          {tabs.map((t) => <div key={t.value} {...tabPanelProps(TAB_ID, t.value)} hidden />)}
        </div>
      </header>

      <div className="nx-page nx-project-body">
        {allowed.length > 0
          ? <Outlet context={context} />
          : <EmptyState title="Bu projede görebileceğiniz bir sekme yok"
              hint="Rolünüz bu projede hiçbir sekmeyi açmıyor. Proje yöneticisinden erişim isteyin." />}
      </div>
    </>
  )
}

/**
 * İzinli sekmeler, arayüz sırasıyla. Ayarlar sekmesi silme yetkisi olana da açılır
 * (silme düğmesi orada).
 */
function allowedTabs(can: Can): ProjectTabKey[] {
  return PROJECT_TABS
    .filter((t) => can(t.permission) || (t.key === 'ayarlar' && can('project.delete')))
    .map((t) => t.key)
}

/** Proje kökü: ilk izinli sekmeye açılır (yalnız test koşan doğrudan Testler'e). */
export function ProjectIndexRedirect() {
  const { project } = useOutletContext<{ project: Project }>()
  const first = allowedTabs(projectCan(project))[0]
  return first ? <Navigate to={first} replace /> : null
}
